import { useEffect, useMemo, useRef, useState } from 'react';
import { ColumnDef, OnChangeFn, SortingState } from '@tanstack/react-table';
import { CalendarBlankIcon } from '@phosphor-icons/react';
import TanStackTable from '@/components/ui/TanStackTable';
import UserAvatarStack from '@/components/ui/UserAvatarStack';
import { getActionDisplayStatus } from '../actionStatus';
import { compareActionPriority } from '../actionPriority';
import ActionStatusPill from './ActionStatusPill';
import ActionPriorityPill from './ActionPriorityPill';
import ActionTagPills from './ActionTagPills';
import { Action, getAssigneesDisplayName } from '../types';
import { formatShortDate } from 'src/lib/utils/formatDate';
import { columnVisibilityForWidth } from './columnVisibility';

interface ActionsDataTableProps {
  data: Action[];
  onRowClick: (action: Action) => void;
  emptyMessage?: string;
  getGroupLabel?: (action: Action) => string | null;
  /** Dedicated Group column instead of a label under the description. */
  showGroupColumn?: boolean;
  itemsPerPage?: number;
  showAssignees?: boolean;
}

/** Keep the trailing `-N` (and the last prefix char) visible; ellipsize the middle. */
function ActionReferenceId({ id }: { id: string }) {
  const dash = id.lastIndexOf('-');
  const start = dash > 0 ? id.slice(0, Math.max(dash - 1, 1)) : id.slice(0, Math.ceil(id.length / 2));
  const end = dash > 0 ? id.slice(Math.max(dash - 1, 1)) : id.slice(Math.ceil(id.length / 2));
  return (
    <span title={id} className="flex min-w-0 max-w-full font-mono text-sm leading-5 text-content-subtle">
      <span className="min-w-0 truncate">{start}</span>
      <span className="shrink-0">{end}</span>
    </span>
  );
}

/** Hide first when the table is tight (zoom or a narrow pane). */
const HIDE_ORDER = ['reference_id', 'group', 'tags', 'priority', 'due_date', 'assignee', 'status'] as const;

const COLUMN_MIN_WIDTH: Record<string, number> = {
  reference_id: 88,
  description: 200,
  group: 160,
  status: 100,
  priority: 110,
  tags: 180,
  due_date: 140,
  assignee: 125
};

const CELL_GUTTER = 32;
const ACTION_COLUMN_IDS_NO_ASSIGNEE = [
  'reference_id',
  'description',
  'group',
  'status',
  'priority',
  'tags',
  'due_date'
];
const ALL_ACTION_COLUMN_IDS = [...ACTION_COLUMN_IDS_NO_ASSIGNEE, 'assignee'];

const ID_TIEBREAKER = { id: 'reference_id', desc: false } as const;

function primarySortFromChange(prev: SortingState, next: SortingState): SortingState {
  if (next.length === 0) {
    return [{ id: 'due_date', desc: false }];
  }
  const prevById = new Map(prev.map((sort) => [sort.id, sort]));
  const added = next.filter((sort) => !prevById.has(sort.id));
  if (added.length > 0) {
    return [added[added.length - 1]];
  }
  const toggled = next.find((sort) => prevById.get(sort.id)?.desc !== sort.desc);
  if (toggled) {
    return [toggled];
  }
  return [next[0]];
}

function withIdTiebreaker(next: SortingState): SortingState {
  if (next.some((sort) => sort.id === 'reference_id')) return next;
  return [...next, { ...ID_TIEBREAKER }];
}

export default function ActionsDataTable({
  data,
  onRowClick,
  emptyMessage = 'No actions to show.',
  getGroupLabel,
  showGroupColumn = false,
  itemsPerPage = 12,
  showAssignees = true
}: ActionsDataTableProps) {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'due_date', desc: false }, { ...ID_TIEBREAKER }]);

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return withIdTiebreaker(primarySortFromChange(prev, next));
    });
  };

  const tableRef = useRef<HTMLDivElement>(null);
  const [tableWidth, setTableWidth] = useState(0);

  useEffect(() => {
    const node = tableRef.current;
    if (!node) return;
    const update = () => setTableWidth(node.offsetWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const columns = useMemo<ColumnDef<Action>[]>(() => {
    const cols: ColumnDef<Action>[] = [
      {
        id: 'reference_id',
        accessorKey: 'reference_id',
        header: 'ID',
        enableSorting: true,
        size: 88,
        minSize: 64,
        cell: (info) => <ActionReferenceId id={info.row.original.reference_id} />,
        sortingFn: (rowA, rowB) =>
          rowA.original.reference_id.localeCompare(rowB.original.reference_id, undefined, { numeric: true })
      },
      {
        id: 'description',
        accessorKey: 'description',
        header: 'Description',
        minSize: 200,
        cell: (info) => {
          const action = info.row.original;
          const hideDedicatedGroup =
            showGroupColumn &&
            columnVisibilityForWidth(
              tableWidth,
              showAssignees ? ALL_ACTION_COLUMN_IDS : ACTION_COLUMN_IDS_NO_ASSIGNEE,
              {
                hideOrder: HIDE_ORDER,
                minWidths: COLUMN_MIN_WIDTH,
                gutter: CELL_GUTTER
              }
            ).group === false;
          const groupLabel = (!showGroupColumn || hideDedicatedGroup) && getGroupLabel ? getGroupLabel(action) : null;
          const descriptionTitle = action.description.split('\n')[0]?.trim() || action.description;
          return (
            <div className="flex flex-col gap-1 min-w-0">
              <span className="text-sm text-content-default font-medium leading-5 truncate" title={action.description}>
                {descriptionTitle}
              </span>
              {groupLabel && (
                <span className="w-fit text-[10px] uppercase tracking-wider font-bold text-content-subtle bg-gray-100 px-1.5 py-0.5 rounded">
                  {groupLabel}
                </span>
              )}
            </div>
          );
        }
      },
      ...(showGroupColumn
        ? [
            {
              id: 'group',
              accessorFn: (row: Action) => getGroupLabel?.(row) ?? '',
              header: 'Group',
              size: 160,
              minSize: 120,
              cell: (info: { row: { original: Action } }) => (
                <span
                  className="block min-w-0 truncate text-sm text-content-subtle"
                  title={getGroupLabel?.(info.row.original) ?? undefined}
                >
                  {getGroupLabel?.(info.row.original) || '—'}
                </span>
              )
            } satisfies ColumnDef<Action>
          ]
        : []),
      {
        id: 'status',
        accessorFn: (row) => getActionDisplayStatus(row),
        header: 'Status',
        size: 100,
        minSize: 100,
        cell: (info) => {
          const displayStatus = getActionDisplayStatus(info.row.original);
          return <ActionStatusPill status={displayStatus} />;
        }
      },
      {
        id: 'priority',
        accessorKey: 'priority',
        header: 'Priority',
        enableSorting: true,
        sortUndefined: 'last',
        size: 110,
        minSize: 96,
        sortingFn: (rowA, rowB) => compareActionPriority(rowA.original.priority, rowB.original.priority),
        cell: (info) => <ActionPriorityPill priority={info.row.original.priority} />
      },
      {
        id: 'tags',
        header: 'Tags',
        enableSorting: false,
        size: 180,
        minSize: 120,
        maxSize: 180,
        cell: (info) => (
          <div className="min-w-0 max-w-full">
            <ActionTagPills tags={info.row.original.tags} maxVisible={2} maxWidth="100%" />
          </div>
        )
      },
      {
        id: 'due_date',
        accessorKey: 'due_date',
        header: 'Due date',
        enableSorting: true,
        sortUndefined: 'last',
        size: 140,
        minSize: 120,
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.due_date;
          const b = rowB.original.due_date;
          if (!a && !b) return 0;
          if (!a) return 1;
          if (!b) return -1;
          return new Date(a).getTime() - new Date(b).getTime();
        },
        cell: (info) => (
          <div className="flex items-center gap-1.5 text-sm text-content-subtle whitespace-nowrap">
            <CalendarBlankIcon size={16} className="text-content-subtle shrink-0" />
            <span>{formatShortDate(info.row.original.due_date, { empty: 'Not set' })}</span>
          </div>
        )
      },
      {
        id: 'assignee',
        accessorFn: (row) => getAssigneesDisplayName(row.assignees),
        header: 'Assignees',
        size: 125,
        minSize: 120,
        cell: (info) => (
          <UserAvatarStack
            people={(info.row.original.assignees || []).map((assignee) => ({
              id: assignee.user_id,
              name: assignee.name,
              email: assignee.email
            }))}
            maxVisible={3}
            emptyLabel="Unassigned"
          />
        )
      }
    ];
    return showAssignees ? cols : cols.filter((column) => column.id !== 'assignee');
  }, [getGroupLabel, showAssignees, showGroupColumn, tableWidth]);

  const columnVisibility = useMemo(() => {
    const columnIds = columns.map((column) => column.id).filter((id): id is string => !!id);
    return columnVisibilityForWidth(tableWidth, columnIds, {
      hideOrder: HIDE_ORDER,
      minWidths: COLUMN_MIN_WIDTH,
      gutter: CELL_GUTTER
    });
  }, [columns, tableWidth]);

  if (data.length === 0) {
    return (
      <div className="text-center py-8 border border-dashed border-border-subtle rounded-xl">
        <p className="text-sm text-content-subtle">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div ref={tableRef} className="w-full min-w-0">
      <TanStackTable
        data={data}
        columns={columns}
        onRowClick={onRowClick}
        itemsPerPage={itemsPerPage}
        enableSorting
        enableMultiSort={false}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        autoRowHeight
        fluidColumnId="description"
        cellVerticalAlign="middle"
        headerSize="comfortable"
        columnVisibility={columnVisibility}
      />
    </div>
  );
}
